import React, { useState, useEffect } from 'react'
import moment from 'moment-timezone'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Heading, LoadingFull as Loading } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import SchedulingPollForm from '../SchedulingPollForm'
import messages from './messages'

const SchedulingPollEditPage = () => {
    const { formatMessage } = useIntl()
    const history = useHistory()
    const location = useLocation()
    const { id } = useParams()

    const [poll, setPoll] = useState(null)
    const [loading, setLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (location.pathname === `/scheduling-polls/${id}/edit`) {
            history.replace(`/scheduling-polls/${id}/edit/calendar`)
        }
    }, [location.pathname, id, history])

    useEffect(() => {
        fetch(`/rest/v2/schedulingpoll/${id}`, {
            method: 'GET',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
        })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.data) {
                    setPoll(data.data)
                } else {
                    toastr.error(formatMessage(messages.loadPollError))
                    history.push(`/scheduling-polls/${id}`)
                }
            })
            .catch(() => {
                toastr.error(formatMessage(messages.loadPollError))
                history.push(`/scheduling-polls/${id}`)
            })
            .finally(() => setLoading(false))
    }, [id])

    const handleSubmit = async ({ title, description, includeBranding, timeSlots }) => {
        setIsSubmitting(true)
        try {
            const timeSlotsPayload = timeSlots.map(slot => {
                const entry = {
                    startDatetime: moment(slot.startDatetime).utc().format('YYYY-MM-DD HH:mm:ss'),
                    endDatetime: moment(slot.endDatetime).utc().format('YYYY-MM-DD HH:mm:ss'),
                }
                if (slot.uid) entry.uid = slot.uid
                return entry
            })

            const response = await fetch(`/rest/v2/schedulingpoll/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    updatePoll: true,
                    title,
                    description,
                    includeBranding,
                    timeSlots: timeSlotsPayload,
                }),
            })

            const data = await response.json()

            if (data.success) {
                toastr.success(formatMessage(messages.pollUpdatedSuccess))
                history.push(`/scheduling-polls/${id}`)
            } else {
                throw new Error(data.errors ? data.errors.join(', ') : formatMessage(messages.updatePollError))
            }
        } catch (error) {
            toastr.error(error.message || formatMessage(messages.updatePollError))
        } finally {
            setIsSubmitting(false)
        }
    }

    if (loading || isSubmitting) {
        return <Loading />
    }

    const initialTimeSlots = (poll?.timeSlots || []).map(slot => ({
        uid: slot.uid,
        startDatetime: moment.utc(slot.start_datetime).local().format('YYYY-MM-DDTHH:mm'),
        endDatetime: moment.utc(slot.end_datetime).local().format('YYYY-MM-DDTHH:mm'),
        isExisting: true,
    }))

    return (
        <div className="flex-1 px-4 py-4">
            <div className="w-full mx-auto">
                <Heading level={1} className="text-2xl font-bold mb-4">
                    <FormattedMessage {...messages.editSchedulingPoll} />
                </Heading>

                <SchedulingPollForm
                    key={id}
                    initialTitle={poll?.title || ''}
                    initialDescription={poll?.description || ''}
                    initialIncludeBranding={!!poll?.include_branding}
                    initialTimeSlots={initialTimeSlots}
                    basePath={`/scheduling-polls/${id}/edit`}
                    onSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    submitLabel={formatMessage(messages.saveChanges)}
                    onCancel={() => history.push(`/scheduling-polls/${id}`)}
                    showExistingIndicators
                />
            </div>
        </div>
    )
}

export default SchedulingPollEditPage
